/* Inspired by https://github.com/mugglim/tanstack-query-optimistic-updates/tree/main (MIT License) */

import { DefaultError, useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { QueryKey } from '@tanstack/react-query';

type OptimisticUpdateOptions<TQueryData = unknown, TVariables = void> = {
    /**
     * A query key for optimistic update
     */
    queryKey: QueryKey;
    /**
     * A function returns the resulting optimistic state.
     */
    optimisticUpdatesFn: (param: { prevQueryData: TQueryData; variables: TVariables }) => TQueryData;
    /**
     * If set to `true`, the query will fetch if the data is stale.
     *
     * @default false
     */
    invalidateQueryOnSuccess?: boolean | (() => boolean);
};

type OptimisticUpdateContext = {
    prevQueryData?: unknown;
};

export function useOptimisticMutation<TData = unknown, TError = DefaultError, TVariables = void, TQueryData = unknown>({
    optimisticUpdateOptions,
    ...originalMutationOptions
}: UseMutationOptions<TData, TError, TVariables, OptimisticUpdateContext> & {
    optimisticUpdateOptions: OptimisticUpdateOptions<TQueryData, TVariables>;
}) {
    const client = useQueryClient();

    const { onMutate, onError, onSuccess, ...restOriginalMutationOptions } = originalMutationOptions;
    const {
        queryKey: optimisticUpdateQueryKey,
        invalidateQueryOnSuccess = false,
        optimisticUpdatesFn,
    } = optimisticUpdateOptions;

    return useMutation<TData, TError, TVariables, OptimisticUpdateContext>({
        onMutate: async (variables, mutationContext) => {
            await client.cancelQueries({ queryKey: optimisticUpdateQueryKey });

            const prevQueryData = client.getQueryData<TQueryData>(optimisticUpdateQueryKey);

            if (!prevQueryData) {
                // If query data is undefined, optimistic update is skipped.
                return { prevQueryData: undefined };
            }

            const optimisticQueryData = optimisticUpdatesFn({ prevQueryData, variables });

            client.setQueryData(optimisticUpdateQueryKey, optimisticQueryData);
            onMutate?.(variables, mutationContext);

            return { prevQueryData };
        },
        onError: (error, variables, onMutateResult, mutationContext) => {
            client.setQueryData(optimisticUpdateQueryKey, onMutateResult?.prevQueryData);

            onError?.(error, variables, onMutateResult, mutationContext);
        },
        onSuccess: (data, variables, onMutateResult, mutationContext) => {
            const shouldInvalidateQuery =
                typeof invalidateQueryOnSuccess === 'function' ? invalidateQueryOnSuccess() : invalidateQueryOnSuccess;

            if (shouldInvalidateQuery) {
                client.invalidateQueries({ queryKey: optimisticUpdateQueryKey });
            }

            onSuccess?.(data, variables, onMutateResult, mutationContext);
        },
        ...restOriginalMutationOptions,
    });
}
